import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  LineChart, 
  Database, 
  Code2, 
  Layers,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { cn } from '../../lib/utils';

const solutions = [
  {
    icon: LineChart,
    badge: "Digital Strategy",
    title: "Empowering Digital Evolution",
    description: "Guiding businesses in their transition to digital-first operations, optimizing efficiency and enhancing customer experiences through cutting-edge technologies."
  },
  {
    icon: Layers,
    badge: "Expert Consulting",
    title: "Strategic Technology Advisory",
    description: "Comprehensive technology consulting services empower businesses to leverage advanced technologies for transformative growth."
  },
  {
    icon: Database,
    badge: "Business Systems",
    title: "Enterprise Solutions",
    description: "Implementing and optimizing enterprise systems that streamline operations, enhance productivity, and deliver exceptional customer experiences across your organization."
  },
  {
    icon: Code2,
    badge: "AI & Analytics",
    title: "Accelerating Data Analytics Initiatives",
    description: "Providing AI-driven insights that transform raw data into actionable intelligence, empowering organizations to make informed decisions and drive strategic growth."
  }
];

const Solutions = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.8;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <h2 className="text-4xl lg:text-5xl font-display font-semibold text-gray-900 mb-4">
            Complete Solutions for Modern Businesses
          </h2>
          <p className="text-xl text-gray-600">
            Empowering your digital journey with comprehensive solutions that drive innovation and growth.
          </p>
        </motion.div>

        <div className="relative">
          {/* Solutions Grid/Scroll Container */}
          <div 
            ref={scrollContainerRef}
            onScroll={checkScrollButtons}
            className="flex md:grid md:grid-cols-2 gap-2 md:gap-8 overflow-x-auto scrollbar-hide snap-x snap-mandatory md:mx-0 pb-8 pl-4 md:pl-0"
          >
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={solution.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="group relative flex-shrink-0 w-[85vw] md:w-full snap-start mr-1 last:mr-4 md:mr-0 bg-gradient-to-br from-gray-800 to-gray-700 backdrop-blur-sm rounded-xl p-8 border border-gray-600 hover:border-gray-500 transition-all duration-300"
                >
                  {/* Hover Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
                  
                  <div className="relative flex flex-col h-full">
                    <span className="inline-block px-4 py-1 rounded-full bg-accent/10 text-accent text-sm font-medium mb-4 self-start">
                      {solution.badge}
                    </span>
                    
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-lg bg-accent/10 group-hover:bg-accent/20 transition-colors duration-300">
                        <Icon className="w-6 h-6 text-accent" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">
                        {solution.title}
                      </h3>
                    </div>

                    <p className="text-gray-300 mb-6 flex-grow">
                      {solution.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Mobile Navigation Buttons */}
          <div className="md:hidden flex justify-center gap-4 mt-8">
            <button
              onClick={() => scroll('left')}
              className={cn(
                "p-2 rounded-full bg-gray-100 backdrop-blur-sm transition-all duration-300",
                "hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed",
                !canScrollLeft && "opacity-30 cursor-not-allowed"
              )}
              disabled={!canScrollLeft}
            >
              <ChevronLeft className="w-6 h-6 text-gray-900" />
            </button>
            <button
              onClick={() => scroll('right')}
              className={cn(
                "p-2 rounded-full bg-gray-100 backdrop-blur-sm transition-all duration-300",
                "hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed",
                !canScrollRight && "opacity-30 cursor-not-allowed"
              )}
              disabled={!canScrollRight}
            >
              <ChevronRight className="w-6 h-6 text-gray-900" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solutions;